import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { companyDetails } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const navItems = [
    { name: "Forside", path: "/" },
    { name: "Lederforløb", path: "/kurser" },
    { name: "Kommende hold", path: "/kommende-hold" },
    { name: "Virksomheder", path: "/virksomheder" },
    { name: "Om Anita", path: "/om-hende" },
    { name: "Anbefalinger", path: "/anbefalinger" },
    { name: "FAQ", path: "/faq" },
    { name: "Kontakt", path: "/kontakt" }
  ];

  return (
    <header className={`sticky top-0 z-50 border-b border-maersk-light shadow-xs transition-all duration-300 ${
      isOpen ? "bg-white" : "bg-white/95 backdrop-blur-md"
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" aria-label="Lederskab.nu - Til forsiden">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=300,format=auto,quality=90/assets/22d442ab-45d9-4214-861d-1df67f744e88/lederskab-nu-logoer-2026-2.png"
              alt="Lederskab.nu - Det landsdækkende lederakademi"
              width="220"
              height="56"
              fetchPriority="high"
              decoding="async"
              className="h-12 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-102"
            />
          </Link>

          {/* Desktop Navigation (Hidden below 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `font-sans text-sm font-medium tracking-wide transition-all duration-300 py-2 border-b-2 ${
                    isActive
                      ? "text-maersk-blue border-maersk-blue"
                      : "text-navy-700 border-transparent hover:text-maersk-blue hover:border-maersk-light"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
            <Link
              to="/kontakt"
              className="ml-4 bg-brand-orange hover:bg-brand-orange-hover text-white px-5 py-2.5 rounded-md text-sm font-semibold tracking-wide transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
            >
              Kom i gang <i className="fa-solid fa-arrow-right ml-2 text-xs" />
            </Link>
          </nav>

          {/* Burger Menu Button (Visible below 1200px) */}
          <div className="flex items-center min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-maersk-dark hover:text-maersk-blue p-2 rounded-md focus:outline-hidden transition-colors duration-300"
              aria-label="Åbn eller luk menu"
            >
              <i className={`fa-solid ${isOpen ? "fa-xmark text-2xl" : "fa-bars text-xl"}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer (Hidden above 1200px) */}
      <div
        className={`fixed inset-0 top-20 z-40 bg-white transition-all duration-300 ease-in-out min-[1200px]:hidden ${
          isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full pointer-events-none"
        }`}
        style={{ backgroundColor: "#ffffff" }}
      >
        <div className="flex flex-col h-[calc(100vh-80px)] justify-between p-6 overflow-y-auto bg-white" style={{ backgroundColor: "#ffffff" }}>
          <nav className="flex flex-col gap-5 bg-white" style={{ backgroundColor: "#ffffff" }}>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-lg font-semibold tracking-wide py-3 border-b border-navy-50 transition-colors duration-300 ${
                    isActive ? "text-maersk-blue pl-2 border-maersk-blue" : "text-maersk-dark hover:text-maersk-blue"
                  }`
                }
              >
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* Mobile Footer Info */}
          <div className="bg-maersk-light p-5 rounded-xl border border-maersk-light/50 space-y-4">
            <h4 className="font-display font-bold text-maersk-dark">Kontakt Lederskab</h4>
            <div className="space-y-2.5 text-sm">
              <p className="flex items-center gap-3 text-navy-700">
                <i className="fa-solid fa-phone text-maersk-blue" />
                <a href={`tel:${companyDetails.phone.replace(/\s+/g, "")}`} className="hover:text-maersk-blue font-medium whitespace-nowrap">
                  {companyDetails.phone}
                </a>
              </p>
              <p className="flex items-center gap-3 text-navy-700">
                <i className="fa-solid fa-envelope text-maersk-blue" />
                <a href={`mailto:${companyDetails.email}`} className="hover:text-maersk-blue font-medium break-all">
                  {companyDetails.email}
                </a>
              </p>
              <p className="flex items-center gap-3 text-navy-700">
                <i className="fa-solid fa-location-dot text-maersk-blue" />
                <span className="font-medium">{companyDetails.address}</span>
              </p>
            </div>
            <div className="flex gap-4 pt-2">
              <a
                href={companyDetails.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-maersk-blue hover:bg-maersk-blue hover:text-white transition-all duration-300 shadow-xs"
                aria-label="Facebook"
              >
                <i className="fa-brands fa-facebook-f" />
              </a>
              <a
                href={companyDetails.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-maersk-blue hover:bg-maersk-blue hover:text-white transition-all duration-300 shadow-xs"
                aria-label="LinkedIn"
              >
                <i className="fa-brands fa-linkedin-in" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
